<template>
  <div
    v-if="selfPerformMetricsAction.isNot(States.LOADING)"
    class="h-full space-y-6 w-full flex flex-col"
  >
    <!-- KPI Cards Section -->
    <div class="h-1/5 flex">
      <KpiSwiper
        :kpi-metrics="kpiMetrics"
        :date="startOfSelectedWeek"
      />
    </div>

    <div
      class="element-island flex duration-200 transition-[height] ease-linear flex-grow-0 max-h-[40%]"
      style="--animate-duration: 0.2s;"
      :class="[showGraphs ? 'h-2/5' : 'h-0 p-0']"
      @transitionend="graphsHidden"
    >
      <Transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
      >
        <template v-if="selectedProject">
          <div
            v-show="showGraphsCanvas"
            class="flex w-full relative"
          >
            <div
              ref="dateSelector"
              class="absolute left-0 flex items-start space-x-1"
            >
              <button
                type="button"
                class="btn-icon--small"
                :class="{ 'btn-icon--small--active': showDateSelector }"
                @click="toggleDateSelector"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 v-if="showDateSelector"
                 
                  class="h-6 w-6">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>

                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                 
                  class="h-6 w-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
</svg>
              </button>

              <Transition
                enter-active-class="animate__animated animate__fadeIn"
                leave-active-class="animate__animated animate__fadeOut"
              >
                <div
                  v-show="showDateSelector"
                  class="flex space-x-3 items-center element-island border border-gray-300"
                >
                  <p class="text-sm font-medium">
                    {{ $t('SelfPerform.Dashboard.weekOf') }}
                  </p>

                  <AppInput
                    v-model="selectedDate"
                    type="date"
                    name="to_date"
                    :max="maxSelectableDate"
                    class="form-input text-sm"
                  />

                  <button
                    type="button"
                    class="btn btn--secondary-blue"
                    @click="dateToFetch = selectedDate; showDateSelector = false"
                  >
                    Apply
                  </button>
                </div>
              </Transition>
            </div>

            <div class="text-center w-1/2">
              <h3 class="text-lg font-semibold text-gray-900 text-center">
                {{ $t('SelfPerform.Dashboard.plantUtilisationWeek', { date: startOfSelectedWeek.toFormat('dd/MMM') }) }}
              </h3>

              <PlantUtilisationGraph
                :selected-date="dateToFetch"
                :project-id="selectedProject.id"
              />
            </div>

            <div class="text-center w-1/2">
              <h3 class="text-lg font-semibold text-gray-900 text-center">
                {{ $t('SelfPerform.Dashboard.weeklyPerformance') }}
              </h3>

              <WeeklyPerformanceGraph
                :selected-date="dateToFetch"
                :project-id="selectedProject.id"
              />
            </div>
          </div>
        </template>
      </Transition>
    </div>

    <div
      class="bg-white rounded-xl shadow-sm duration-200 transition-[height] ease-linear relative overflow-auto flex-shrink-0"
      :class="[setFullHeight ? 'h-4/5' : 'h-2/5']"
    >
      <!-- Toggle Button -->


      <template v-if="selectedProject">
        <div
          v-if="wbsCodes.length === 0"
          class="h-full flex flex-col items-center justify-center p-8"
        >
          <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-400">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
          </div>

          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ $t('SelfPerform.Dashboard.projectSetupRequired') }}
          </h3>

          <p class="text-gray-600 text-center mb-6">
            {{ $t('SelfPerform.Dashboard.setupInstructions') }}
          </p>

          <RouterLink
            v-if="selectedProject && hasSettingsAccess"
            :to="{
              name: 'ProjectSettings', params: { projectId: selectedProject.id }, hash: '#self_perform'
            }"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>
            {{ $t('SelfPerform.Dashboard.configureSettings') }}
          </RouterLink>
        </div>

        <!-- WBS Code Breakdown with Header -->
        <div
          v-else
          class="h-full flex flex-col"
        >
          <!-- Header -->
          <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-3 rounded-t-xl">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div
                  @click="toggleGraph"
                >
                  <button
                    type="button"
                    class="rounded-full flex items-center justify-center transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="setFullHeight"
                     
                      class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                     
                      class="w-6 h-6 text-white">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                  </button>
                </div>

                <h2 class="font-semibold text-white">
                  WBS Code Performance
                </h2>
              </div>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white">
    <path clip-rule="evenodd" d="m7.33755 4.30894c.30652-1.18429 1.38234-2.05894 2.66245-2.05894h4c1.2801 0 2.3559.87465 2.6624 2.05894 1.5999.28654 2.8416 1.57693 3.0555 3.20159.0322.24459.0322.52292.0321.93158v.00003.05786 7.7.0321c0 .8129 0 1.4685-.0434 1.9994-.0446.5466-.139 1.0267-.3653 1.471-.3596.7056-.9332 1.2793-1.6388 1.6388-.4443.2263-.9244.3207-1.471.3653-.5309.0434-1.1865.0434-1.9994.0434h-.0321-4.4-.03213c-.81283 0-1.46844 0-1.99934-.0434-.54664-.0446-1.02678-.139-1.471-.3653-.7056-.3595-1.27928-.9332-1.63881-1.6388-.22634-.4443-.3207-.9244-.36536-1.471-.04338-.5309-.04337-1.1866-.04336-1.9994v-.0321-7.7l-.00001-.05786c-.00007-.40868-.00011-.68702.03209-.93161.21389-1.62466 1.45558-2.91505 3.05547-3.20159zm.04216 1.52812c-.85075.24354-1.49225.97131-1.61046 1.86926-.01764.13392-.01925.30542-.01925.79368v7.7c0 .8525.00058 1.4467.03838 1.9093.03708.4539.10621.7147.20685.9122.21572.4233.55992.7675.98329.9833.19752.1006.45828.1697.91216.2068.46263.0378 1.05686.0384 1.90932.0384h4.4c.8525 0 1.4467-.0006 1.9093-.0384.4539-.0371.7147-.1062.9122-.2068.4233-.2158.7675-.56.9833-.9833.1006-.1975.1697-.4583.2068-.9122.0378-.4626.0384-1.0568.0384-1.9093v-7.7c0-.48826-.0016-.65976-.0193-.79368-.1182-.89795-.7597-1.62572-1.6104-1.86926-.3541 1.10947-1.3934 1.91294-2.6203 1.91294h-4c-1.22691 0-2.26617-.80347-2.62029-1.91294zm2.62029-2.08706c-.69036 0-1.25.55964-1.25 1.25s.55964 1.25 1.25 1.25h4c.6904 0 1.25-.55964 1.25-1.25s-.5596-1.25-1.25-1.25zm-2.75 7.25c0-.4142.33578-.75.75-.75h8c.4142 0 .75.3358.75.75s-.3358.75-.75.75h-8c-.41422 0-.75-.3358-.75-.75zm.75 2.25c-.41422 0-.75.3358-.75.75s.33578.75.75.75h8c.4142 0 .75-.3358.75-.75s-.3358-.75-.75-.75zm-.75 3.75c0-.4142.33578-.75.75-.75h6c.4142 0 .75.3358.75.75s-.3358.75-.75.75h-6c-.41422 0-.75-.3358-.75-.75z" fill="currentColor" fill-rule="evenodd"/>
</svg>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-auto">
            <WbsCodeBreakdown
              :project-id="selectedProject.id"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import KpiSwiper from '@/components/ProjectSelector/KpiSwiper.vue';
import PlantUtilisationGraph from '@/components/ProjectSelector/PlantUtilisationGraph.vue';
import WbsCodeBreakdown from '@/components/ProjectSelector/WbsCodeBreakdown.vue';
import WeeklyPerformanceGraph from '@/components/ProjectSelector/WeeklyPerformanceGraph.vue';
import useScreenSize, { ScreenSize } from '@/composables/useScreenSize';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, dateForInput } from '@/helpers';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { onClickOutside } from '@vueuse/core';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', selectedProject.value?.id).get();
});

const selectedProject = computed(() => {
  return projectsStore.project;
});

const showGraphs = ref(true);
const showGraphsCanvas = ref(true);
const setFullHeight = ref(false);

const toggleGraph = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = false;
    showGraphs.value = false;
    setFullHeight.value = true;
  } else {
    if(setFullHeight.value) {
      showGraphs.value = true;
      setFullHeight.value = false;
    }
  }
};

const graphsHidden = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = true;
  } else {
    showGraphsCanvas.value = false;
  }
};

const showSidebar = ref(false);
const { screenSize } = useScreenSize();

watch(screenSize, (newValue) => {
  if(newValue >= ScreenSize.XL) {
    showSidebar.value = true;
  }
}, { immediate: true });

type metrics = {
  plant_utilisation_week: number;
  plant_utilisation: number;
  labour_hours: number;
  plant_hours: number;
  total_variance: number;
  variance_week: number;
};

const kpiMetrics = ref<metrics | undefined>();
const selfPerformMetricsAction = useStoreApiAction(projectsStore.selfPerformMetrics);
const indexWbsCodesAction = useStoreApiAction(wbsCodesStore.index);
// TODO: 25/04/24 update week for now, will set up quantities to change in future
const selectedDate = ref<string>(dateForDateInput(new Date()));
const dateToFetch = ref<string>(selectedDate.value);

const startOfSelectedWeek = computed(() => {
  return DateTime.fromISO(dateToFetch.value).startOf('week');
});

watch([selectedProject, dateToFetch], (newValue) => {
  if(newValue[0]) {
    selfPerformMetricsAction.request(newValue[0].id, { date: newValue[1] }).then((metrics) => {
      kpiMetrics.value = metrics;
    });

    indexWbsCodesAction.request(newValue[0].id);
  }
}, { immediate: true });

const showDateSelector = ref(false);
const dateSelector = ref();

const toggleDateSelector = () => {
  if(showDateSelector.value) {
    showDateSelector.value = false;
    selectedDate.value = dateToFetch.value;
  } else {
    showDateSelector.value = true;
  }
};

onClickOutside(dateSelector, (event) => {
  showDateSelector.value = false;
  selectedDate.value = dateToFetch.value;
});

const maxSelectableDate = computed(() => {
  return DateTime.now().endOf('week').toISODate();
});

const userStore = useUserStore();

const hasSettingsAccess = computed(() => {
  return userStore.hasAccessToRoute('ProjectSettings');
});
</script>

<style scoped></style>
